/**
 * ProjectRequestModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A modal component for submitting project requests with:
 * - Comprehensive form validation for all required fields
 * - Date picker integration for target date selection
 * - Fullscreen capability for enhanced form experience
 * - Email submission simulation with confirmation state
 * - Error handling and display for user feedback
 * - Responsive design for various screen sizes
 * - Form reset functionality on modal close
 * 
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {boolean} allowFullscreen - Whether fullscreen mode is available
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Modal.css';
import './ProjectRequestModal.css';

/**
 * ProjectRequestModal component for handling project submission requests
 * with comprehensive form validation and user feedback
 */
function ProjectRequestModal({ show, onClose, allowFullscreen = false }) {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [keyParties, setKeyParties] = useState('');
    const [writingGroup, setWritingGroup] = useState('');
    const [targetDate, setTargetDate] = useState(null);
    const [emailSent, setEmailSent] = useState(false);
    const [errors, setErrors] = useState({});

    // Toggles the fullscreen mode of the modal
    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    // Validate the form inputs, checking if all fields are filled
    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) newErrors.name = 'Name is required';
        if (!description.trim()) newErrors.description = 'Description is required';
        if (!keyParties.trim()) newErrors.keyParties = 'Key Impacted Parties is required';
        if (!writingGroup.trim()) newErrors.writingGroup = 'Writing Group is required';
        if (!targetDate) newErrors.targetDate = 'Proposed Target Date is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Returns true if no errors
    };

    // Handles the form submission process
    const handleSubmit = (e) => {
        e.preventDefault(); // Prevents the default form behavior of reloading the page

        if (validateForm()) {
            // If form is valid, prepare form data for sending
            const formData = {
                name,
                description,
                keyParties,
                writingGroup,
                targetDate,
            };
            setEmailSent(true); // Simulate email sent confirmation
            // Form data ready for backend API integration
        }
    };

    // Handles closing the modal and resetting email sent state
    const handleCancel = () => {
        onClose();
        setEmailSent(false); // Reset emailSent when modal is closed
    };

    // If modal is not shown, return null to avoid rendering
    if (!show) return null;

    return (
        <div id="projectRequestModal" className={`custom-modal ${isFullScreen ? 'fullscreen' : ''}`}>
            <div className="modal-content">
                <div className="modal-header">
                    <span id="modalTitle">Submit a Project</span>
                    <div>
                        {allowFullscreen && (
                            <span
                                className="modalfullscreen fullscreen-btn fas fa-expand" onClick={toggleFullScreen}
                            ></span>
                        )}
                        <span
                            className="modalclose pointer fas fa-times"
                            onClick={handleCancel}
                        ></span>
                    </div>
                </div>
                <div className="modal-body">
                    {emailSent ? (
                        <span>Email Sent! (Not really, we need to create an API to send email using SendGrid or similar)</span>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Name</label>
                                <input 
                                    type="text" 
                                    id="name" 
                                    className="form-control" 
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                />
                                {errors.name && <small className="error">{errors.name}</small>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea 
                                    id="description" 
                                    className="form-control" 
                                    value={description} 
                                    onChange={(e) => setDescription(e.target.value)} 
                                />
                                {errors.description && <small className="error">{errors.description}</small>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="keyParties">Key Impacted Parties</label>
                                <input 
                                    type="text" 
                                    id="keyParties" 
                                    className="form-control" 
                                    value={keyParties} 
                                    onChange={(e) => setKeyParties(e.target.value)} 
                                />
                                {errors.keyParties && <small className="error">{errors.keyParties}</small>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="writingGroup">Writing Group</label>
                                <input 
                                    type="text" 
                                    id="writingGroup" 
                                    className="form-control" 
                                    value={writingGroup} 
                                    onChange={(e) => setWritingGroup(e.target.value)} 
                                />
                                {errors.writingGroup && <small className="error">{errors.writingGroup}</small>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="targetDate">Proposed Target Date</label>
                                <DatePicker
                                    selected={targetDate}
                                    onChange={(date) => setTargetDate(date)}
                                    className="form-control"
                                    dateFormat="MMMM d, yyyy"
                                    placeholderText="Select a date"
                                    minDate={new Date()} 
                                />
                                {errors.targetDate && <small className="error">{errors.targetDate}</small>}
                            </div>

                            <div className='buttonHolder'>
                                <button type="button" className="cancelbtn btn" onClick={handleCancel}>Cancel</button>
                                <button type="submit" className="submitbtn btn btn-primary">Submit</button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProjectRequestModal;
